import { useTheme } from "@mui/material";
import { formatAmount } from "../../../utils";

// ApexCharts injects custom tooltips as raw HTML, so user-entered text must be escaped
const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const renderSalesTooltip = (title, item, grossKey, netKey) =>
  `<div style="padding:8px 10px">
    <div style="font-size:12px;font-weight:700;color:#111827;margin-bottom:6px;">${escapeHtml(
      title
    )}</div>
    <div style="color:#A04D3D;font-size:12px;font-weight:600;">Gross Sales: ${formatAmount(
      item[grossKey]
    )}</div>
    <div style="color:#564845;font-size:12px;font-weight:600;">Net Sales: ${formatAmount(
      item[netKey]
    )}</div>
    <div style="color:#5e7360;font-size:12px;font-weight:600;">ATV: ${formatAmount(
      item.atv
    )}</div>
    <div style="color:#374151;font-size:12px;font-weight:600;">Transactions: ${Number(
      item.transaction_count || 0
    ).toLocaleString()}</div>
  </div>`;

export const formatCompactValue = (value, prefix = "") => {
  const number = Number(value) || 0;
  const absoluteNumber = Math.abs(number);
  if (absoluteNumber >= 1000000000) {
    return `${prefix}${(number / 1000000000).toFixed(2)}B`;
  }
  if (absoluteNumber >= 1000000) {
    return `${prefix}${(number / 1000000).toFixed(2)}M`;
  }
  if (absoluteNumber >= 1000) {
    return `${prefix}${(number / 1000).toFixed(2)}K`;
  }
  return `${prefix}${number.toFixed(2)}`;
};

// Options shared by every dashboard chart so they all read the same way:
// app font, hairline horizontal gridlines, muted axis text, no toolbar.
// Charts spread this first and then add their own axes and marks.
export const useBaseChartOptions = () => {
  const theme = useTheme();
  const axisLabelStyle = {
    colors: theme.palette.text.secondary,
    fontSize: "12px",
  };
  return {
    chart: {
      fontFamily: theme.typography.fontFamily,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    grid: {
      show: true,
      borderColor: theme.palette.divider,
      strokeDashArray: 0,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 16, right: 16, left: 8 },
    },
    xaxis: {
      axisBorder: { show: true, color: theme.palette.divider },
      axisTicks: { show: false },
      labels: {
        style: axisLabelStyle,
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        trim: false,
      },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: { style: axisLabelStyle },
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    tooltip: { marker: { show: false } },
  };
};

// Cap the number of x-axis labels so day labels never crowd each other;
// ApexCharts then shows every nth category label
export const getXAxisTickAmount = (pointCount) => Math.min(pointCount, 10);
